/* The notification list shown when the bell is opened */
import { token } from '@amzn/stencil-design-tokens/js/web/utils';
import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Col, Hr, Row, View } from '@amzn/stencil-react-components/layout';
import { Link } from '@amzn/stencil-react-components/link';
import { Modal, ModalBody, ModalContainer, ModalFooter, ModalHeader } from '@amzn/stencil-react-components/modal';
import { Pagination } from '@amzn/stencil-react-components/pagination';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import { UtilityButton } from '@amzn/stencil-react-components/utility-button';
import { H3, Text, TextVariant } from '@amzn/stencil-react-components/text';
import IconCrossSmall from '@amzn/stencil-react-icons/icons/icon-cross-small';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Status } from 'src/models/AuthContextModels';
import { AppDispatch, RootState } from 'src/store/store';
import { appendEmbedParamsToPath } from 'src/utils/navigation-utils';
import { isActionOutstanding, isDismissible, isUnread, prioritizeActionable, XptNotification } from './notificationModels';
import { formatPageSummary } from './notificationPagination';
import { getNotificationPresentation } from './notificationPresentation';
import { markNotificationsReadThunk } from './notificationsSlice';
import { getSeverityStencilPresentation } from './notificationStencilPresentation';
import { useDismissedNotifications } from './useDismissedNotifications';
import { useNotificationPagination } from './useNotificationPagination';

const MODAL_TITLE_ID = 'notification-panel-title';

// Set on the container's child, not via maxWidth: Modal renders the dialog as a flex item with no
// width, so maxWidth alone lets it shrink to fit content.
const MODAL_WIDTH = '720px';

// A minimum, not a fixed height: the page size caps the list at five rows so nothing scrolls, but
// short pages must not shrink the modal.
const LIST_MIN_HEIGHT = '440px';

// Absolute sizes rather than the rem-based text.size.* tokens, which render smaller here than the
// token value implies. shouldUseFixedFontStyle keeps them out of a media query.
const HEADER_FONT_SIZE = '24px';
const BUTTON_FONT_SIZE = '16px';
const TITLE_FONT_SIZE = '16px';
const BODY_FONT_SIZE = '15px';
const META_FONT_SIZE = '14px';
const COUNT_FONT_SIZE = '14px';
const COUNT_PILL_MIN_SIZE = '24px';

// Fixed so every row's text starts on the same line. Stencil publishes no sizing tokens.
const ICON_COLUMN_WIDTH = '20px';

// Hr resolves colors through the legacy theme adapter only, so a token name yields invalid CSS and
// the divider disappears. token() returns the var() reference.
const DIVIDER_COLOR = token('color.border.primary');

interface NotificationPanelProps {
  visible: boolean;
  onDismiss: () => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ visible, onDismiss }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const allItems = useSelector((state: RootState) => state.notificationsStore.items);
  const status = useSelector((state: RootState) => state.notificationsStore.status);

  const { visibleItems: items, dismiss: dismissNotification } = useDismissedNotifications(allItems);

  // Awaiting-action first, so a request needing review is not pushed onto a later page.
  const orderedItems = useMemo(() => prioritizeActionable(items), [items]);

  const { page, selectPage, resetToFirstPage } = useNotificationPagination(orderedItems);

  // Reopening starts at the newest notifications, not wherever the user last paged to.
  useEffect(() => {
    if (visible) {
      resetToFirstPage();
    }
  }, [visible, resetToFirstPage]);

  const unreadCount = items.filter(isUnread).length;

  // Mark read on close, not on open, so the user still sees which rows were new. Waits for the
  // baseline fetch: an in-flight fetch's fulfilled handler would revert the optimistic grey.
  const closePanel = () => {
    const hasMarkableUnread = items.some((notification) => !notification.is_read && !isActionOutstanding(notification));
    if (hasMarkableUnread && status === Status.Completed) {
      dispatch(markNotificationsReadThunk());
    }
    onDismiss();
  };

  // Takes a path that already carries the embed params, so the href, a plain click, and a
  // middle-click all land on the same URL.
  const onOpenRoute = (fullPath: string) => {
    closePanel();
    navigate(fullPath);
  };

  const renderRow = (notification: XptNotification) => {
    const presentation = getNotificationPresentation(notification);
    const severity = getSeverityStencilPresentation(presentation.severity);
    const unread = isUnread(notification);
    const { Icon: SeverityIcon } = severity;
    // Built once and used for both the href and the handler: the href is what middle-click and
    // "open in new tab" follow, bypassing onClick entirely.
    const href = presentation.route ? appendEmbedParamsToPath(presentation.route) : null;

    return (
      <Row
        alignItems="flex-start"
        gridGap="dimensions.spacing.300"
        padding="dimensions.spacing.300"
        className={href ? 'notification-row' : undefined}
        onClick={href ? () => onOpenRoute(href) : undefined}
      >
        <Col alignItems="center" justifyContent="center" flexShrink={0} width={ICON_COLUMN_WIDTH} minWidth={ICON_COLUMN_WIDTH}>
          <SeverityIcon color={severity.iconColor} aria-hidden={true} />
        </Col>

        <Col flex={1} gridGap="dimensions.spacing.50">
          {/* Weight is the only unread marker, so every row keeps the same height. The title is
              the row's real link — the row-wide click is a mouse convenience, but keyboard users
              need a focusable target. */}
          <Row alignItems="baseline" gridGap="dimensions.spacing.200">
            {href ? (
              <Link
                href={href}
                onClick={(event) => {
                  // The row handler would fire too; navigate once.
                  event.preventDefault();
                  event.stopPropagation();
                  onOpenRoute(href);
                }}
              >
                <Text
                  variant={TextVariant.LabelMD}
                  fontSize={TITLE_FONT_SIZE}
                  fontWeight={unread ? 'bold' : 'regular'}
                  shouldUseFixedFontStyle={true}
                >
                  {presentation.title}
                </Text>
              </Link>
            ) : (
              <Text
                variant={TextVariant.LabelMD}
                fontSize={TITLE_FONT_SIZE}
                fontWeight={unread ? 'bold' : 'regular'}
                color="color.text.primary"
                shouldUseFixedFontStyle={true}
              >
                {presentation.title}
              </Text>
            )}
          </Row>

          <Text variant={TextVariant.BodyMD} fontSize={BODY_FONT_SIZE} color="color.text.secondary" shouldUseFixedFontStyle={true}>
            {presentation.message}
          </Text>
        </Col>

        {/* Only resolved items can be dismissed — hiding a pending one would lose the task. Such
            rows get no control at all, rather than a disabled X that reads as broken. */}
        {isDismissible(notification) && (
          <UtilityButton
            size={ButtonSize.Small}
            icon={<IconCrossSmall title={`Dismiss: ${presentation.title}`} />}
            onClick={(event) => {
              // Must not also navigate via the row handler.
              event.stopPropagation();
              dismissNotification(notification.notification_id);
            }}
          />
        )}
      </Row>
    );
  };

  // Only the first load shows a spinner; a background re-fetch keeps the current list.
  const isInitialLoad = status === Status.Loading && items.length === 0;

  const renderMessage = (message: string) => (
    <Row alignItems="center" justifyContent="center" minHeight={LIST_MIN_HEIGHT}>
      <Text variant={TextVariant.BodyMD} fontSize={BODY_FONT_SIZE} color="color.text.secondary" shouldUseFixedFontStyle={true}>
        {message}
      </Text>
    </Row>
  );

  const renderListArea = () => {
    if (isInitialLoad) {
      return (
        <Row alignItems="center" justifyContent="center" minHeight={LIST_MIN_HEIGHT}>
          <Spinner />
        </Row>
      );
    }
    if (status === Status.Failed && items.length === 0) {
      return renderMessage('Unable to load notifications');
    }
    if (items.length === 0) {
      return renderMessage('No notifications');
    }

    return (
      <Col gridGap="dimensions.spacing.none">
        {page.pageItems.map((notification, index) => (
          <React.Fragment key={notification.notification_id}>
            {index > 0 && <Hr color={DIVIDER_COLOR} />}
            {renderRow(notification)}
          </React.Fragment>
        ))}
      </Col>
    );
  };

  return (
    <Modal isOpen={visible} close={closePanel} isScrollable={false} aria-labelledby={MODAL_TITLE_ID}>
      <ModalContainer maxWidth={MODAL_WIDTH}>
        <View width={MODAL_WIDTH} maxWidth="100%">
          {/* No onCloseButtonClick, so no header X — closing is the footer button, Escape, or
            clicking outside, all of which run closePanel. */}
          <ModalHeader titleId={MODAL_TITLE_ID}>
            <Row alignItems="center" gridGap="dimensions.spacing.200">
              <H3 variant={TextVariant.HeadingLG} fontSize={HEADER_FONT_SIZE} shouldUseFixedFontStyle={true}>
                Notifications
              </H3>
              {/* Not Stencil's Badge: it renders its value at a fixed label-xs with no size prop. */}
              {unreadCount > 0 && (
                <View
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  minWidth={COUNT_PILL_MIN_SIZE}
                  height={COUNT_PILL_MIN_SIZE}
                  padding={{ left: 'dimensions.spacing.100', right: 'dimensions.spacing.100' }}
                  borderRadius="dimensions.border.radius.round"
                  backgroundColor="color.fill.vibrant.blue"
                  aria-label={`${unreadCount} unread`}
                >
                  <Text fontSize={COUNT_FONT_SIZE} fontWeight="bold" color="color.text.primary-inverted" shouldUseFixedFontStyle={true}>
                    {unreadCount}
                  </Text>
                </View>
              )}
            </Row>
          </ModalHeader>

          <ModalBody>
            <Col gridGap="dimensions.spacing.300">
              <View minHeight={LIST_MIN_HEIGHT}>{renderListArea()}</View>

              {/* Always rendered, so it never appears and disappears under the list. */}
              <Row alignItems="center" justifyContent="space-between" gridGap="dimensions.spacing.200">
                <Text variant={TextVariant.BodyHint} fontSize={META_FONT_SIZE} color="color.text.secondary" shouldUseFixedFontStyle={true}>
                  {formatPageSummary(page)}
                </Text>
                {page.numberOfPages > 1 && (
                  <View className="notification-pagination">
                    <Pagination
                      numberOfPages={page.numberOfPages}
                      selectedPage={page.currentPage}
                      onPageSelect={selectPage}
                      aria-label="Notification page selection"
                    />
                  </View>
                )}
              </Row>
            </Col>
          </ModalBody>

          <ModalFooter>
            <Row justifyContent="flex-end" alignItems="center">
              <Button variant={ButtonVariant.Secondary} onClick={closePanel}>
                <Text fontSize={BUTTON_FONT_SIZE} fontWeight="medium" shouldUseFixedFontStyle={true}>
                  Close
                </Text>
              </Button>
            </Row>
          </ModalFooter>
        </View>
      </ModalContainer>
    </Modal>
  );
};
